import React, { useCallback, useState } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useTranslator } from '@jutro/locale';
import { readViewModelValue } from '@xengage/gw-jutro-adapters-react';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { TooltipContentUtil } from 'wni-portals-tooltip';
import { TooltipIcon } from '@jutro/components';
import { Flex } from '@jutro/layout';
import PolicyCoverageUtil from '../../../utils/PolicyCoverageUtil';
import CoverageTermsIterableComponent from '../../CoverageTermsIterableComponent/CoverageTermsIterableComponent';
import UnderlyingPolicyDetailIterable from '../UnderlyingPolicyDetailIterable/UnderlyingPolicyDetailIterable';
import metadata from './UmbrellaCoverageTable.metadata.json5';
import messages from './UmbrellaCoverageTable.messages';
import styles from './UmbrellaCoverageTable.module.scss';

function UmbrellaCoverageTable(props) {
    const { data } = props;
    const { coverageDTOs: lineCoverages } = data;
    const [openItems, setOpenItems] = useState([]);
    const translator = useTranslator();

    const toggleShowHide = useCallback((item) => {
        const openedItem = _.clone(openItems);
        const index = _.indexOf(openedItem, item);
        if (index === -1) {
            openedItem.push(item);
            setOpenItems(openedItem);
        } else {
            openedItem.splice(index, 1);
            setOpenItems(openedItem);
        }
    }, [openItems]);

    const renderCoverageName = (cov, index) => {
        return (
            <Flex justifyContent="left" gap="none" alignContent="middle" className={styles.coverageNameWrapper}>
                <span>{cov.name}</span>
                {TooltipContentUtil[cov.coverageCodeIdentifier_Ext] && 
                    <TooltipIcon
                        id={`coverage_tooltip_${cov.coverageCodeIdentifier_Ext}_${index}`}
                        text={TooltipContentUtil[cov.coverageCodeIdentifier_Ext]}
                    />
                }
            </Flex>
        );
    };

    const generateCoverageOverrides = () => {
        const overrides = lineCoverages.map((cov, index) => {
            return {
                [`coverageNameContainer${index}`]: {
                    content: renderCoverageName(cov, index)
                }
                
            };
        });
        return Object.assign({}, ...overrides);
    };

    const generateScheduleItemsOverrides = () => {
        const overrides = lineCoverages.map((cov, index) => {
            const scheduleItems = PolicyCoverageUtil.getScheduleItems(cov);
            return {
                [`coverageScheduleTermsContainer${index}`]: {
                    visible: PolicyCoverageUtil.hasScheduleItems(cov)
                }, 
                [`showContentToggleLink${index}`]: {
                    onClick: () => toggleShowHide(`${cov.coverageCodeIdentifier_Ext}_${index}`),
                    icon: _.includes(openItems, index) ? 'gw-arrow-drop-up' : 'gw-arrow-drop-down',
                    content: _.includes(openItems, `${cov.coverageCodeIdentifier_Ext}_${index}`)
                        ? translator(messages.hideScheduleItems)
                        : translator(messages.showScheduleItems)
                },
                [`coverageScheduleTermsTable${index}`]: {
                    visible: _.includes(openItems, `${cov.coverageCodeIdentifier_Ext}_${index}`),
                    data: scheduleItems,
                    content: PolicyCoverageUtil.getScheduleItemTableContent(scheduleItems)
                }
            };
        });
        return Object.assign({}, ...overrides);
    };

    const overrides = {
        '@field': {
            readOnly: true
        },
        lineCoverableTitle: {
            value: translator(messages.puLineCoverages)
        },
        lineCoverablesIterable: {
            data: lineCoverages
        },
        ...generateCoverageOverrides(),
        ...generateScheduleItemsOverrides()
    };

    const resolvers = {
        resolveComponentMap: {
            coveragetermsiterablecomponent: CoverageTermsIterableComponent,
            underlyingpolicydetailiterable: UnderlyingPolicyDetailIterable
        },
        resolveClassNameMap: styles
    };

    const readValue = (id, path) => {
        return readViewModelValue(metadata.componentContent, data, id, path, overrides);
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            model={data}
            overrideProps={overrides}
            componentMap={resolvers.resolveComponentMap}
            classNameMap={resolvers.resolveClassNameMap}
            resolveValue={readValue}
        />
    );
}

UmbrellaCoverageTable.propTypes = {
    data: PropTypes.shape({
        coverageDTOs: PropTypes.arrayOf(PropTypes.shape({})),
        ulPolicyDetails: PropTypes.arrayOf(PropTypes.shape({}))
    }).isRequired
};

export default UmbrellaCoverageTable;
